import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';

import { KEYFRAMES_CSS, SIZE_BOX, LEAVES, S, INITIAL_TRANSFORMS, ANIM_NAMES, DUR, EASE } from './constants';
type LeavesType = (typeof LEAVES)[number];

type LeafSVGProps = { leaf: LeavesType };

const LeafSVG = React.memo<LeafSVGProps>(({ leaf }: LeafSVGProps) => (
    <svg width="100%" height="100%" viewBox={`0 0 ${leaf.size} ${leaf.size}`} fill="none">
        <mask id={`mask-${leaf.id}`}>
            <rect width={leaf.size} height={leaf.size} fill="white" />
            <circle cx={leaf.cx} cy={leaf.cy} r={leaf.r} fill="black" />
        </mask>
        <path d={leaf.path} fill={leaf.color} mask={`url(#mask-${leaf.id})`} />
    </svg>
));
LeafSVG.displayName = 'LeafSVG';

interface LogoAnimationProps {
    isReady?: boolean;
    onComplete?: () => void;
}

const LogoAnimation = ({ isReady, onComplete }: LogoAnimationProps) => {
    const [stopped, setStopped] = useState(false);
    const onCompleteRef = useRef(onComplete);
    const readyRef = useRef(false);
    const firstLeafRef = useRef<HTMLDivElement>(null);
    onCompleteRef.current = onComplete;

    useEffect(() => {
        if (isReady) readyRef.current = true;
    }, [isReady]);

    useEffect(() => {
        const el = firstLeafRef.current;
        if (!el) return;

        let iterationCount = 0;
        const handler = () => {
            iterationCount++;
            if (iterationCount >= 1 && readyRef.current) {
                setStopped(true);
                setTimeout(() => onCompleteRef.current?.(), 500);
            }
        };

        el.addEventListener('animationiteration', handler);

        const fallback = setTimeout(() => {
            if (readyRef.current) onCompleteRef.current?.();
        }, 4000);

        return () => {
            el.removeEventListener('animationiteration', handler);
            clearTimeout(fallback);
        };
    }, []);

    return (
        <>
            <GlobalStyles styles={KEYFRAMES_CSS} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'var(--app-vh, 100vh)',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%)',
                    gap: 4,
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ position: 'relative', ...SIZE_BOX }}>
                    {LEAVES.map((leaf, i) => (
                        <div
                            ref={i === 0 ? firstLeafRef : undefined}
                            key={leaf.id}
                            style={{
                                position: 'absolute',
                                width: S,
                                height: S,
                                transform: INITIAL_TRANSFORMS[i],
                                animation: `${ANIM_NAMES[i]} ${DUR} ${EASE} infinite`,
                                animationPlayState: stopped ? 'paused' : 'running'
                            }}
                        >
                            <LeafSVG leaf={leaf} />
                        </div>
                    ))}
                </Box>
            </Box>
        </>
    );
};

export default LogoAnimation;
